import { TripProgress } from "@/components/trip-progress";
import { Badge, Card, ClickableCard, Text, VStack } from "@astryxdesign/core";
import * as stylex from "@stylexjs/stylex";
import { useQuery } from "@tanstack/react-query";
import { ChevronRight, Inbox } from "lucide-react";
import { Link, useNavigate } from "react-router";
import { AppShell } from "@/components/app-shell";
import { FullScreenLoader } from "@/components/spinner";
import { api } from "@/lib/api";
import { formatTripLabel } from "@/lib/trip-label";
import { useOperatorTripUpdates } from "@/services/operator-trip-updates";
import { useSession } from "@/services/auth0/session";
import type { Trip } from "@/types/api";
import type { BadgeVariant } from "@/types/ui";

// GET /api/v2/operator/trips returns { data: Trip[], metadata }. Stay
// tolerant of a bare array or { trips } too.
function unwrapTrips(payload: unknown): Trip[] {
  if (Array.isArray(payload)) return payload as Trip[];
  if (payload && typeof payload === "object") {
    const obj = payload as Record<string, unknown>;
    if (Array.isArray(obj.data)) return obj.data as Trip[];
    if (Array.isArray(obj.trips)) return obj.trips as Trip[];
  }
  return [];
}

const STATUS_VARIANT: Record<string, BadgeVariant> = {
  PLANNED: "info",
  ACTIVE: "success",
  COMPLETED: "neutral",
  CANCELLED: "error",
};

const styles = stylex.create({
  title: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: 8,
    marginBottom: 16,
  },
  scheduleLink: {
    color: "var(--color-text-secondary)",
    fontSize: "0.875rem",
    textDecoration: "underline",
    textUnderlineOffset: 2,
  },
  list: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    listStyle: "none",
    margin: 0,
    padding: 0,
  },
  row: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    width: "100%",
  },
  right: { flexShrink: 0, display: "flex", alignItems: "center", gap: 8 },
  empty: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 8,
    paddingBlock: 64,
    textAlign: "center",
    color: "var(--color-text-secondary)",
  },
  error: { color: "var(--color-error)" },
  muted: { color: "var(--color-text-secondary)", display: "inline-flex" },
});

export function MyTripsScreen() {
  const { operatorId } = useSession();
  const navigate = useNavigate();
  useOperatorTripUpdates(operatorId);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["operator-trips", operatorId],
    queryFn: async () => {
      const res = await api.get("/api/v2/operator/trips");
      return unwrapTrips(res.data);
    },
    enabled: !!operatorId,
    refetchOnWindowFocus: true,
  });

  if (isLoading) return <FullScreenLoader label="Loading your trips…" />;

  const trips = data ?? [];

  return (
    <AppShell>
      <div {...stylex.props(styles.title)}>
        <Text as="h1" size="lg" weight="semibold">
          My trips
        </Text>
        <Link to="/schedule" {...stylex.props(styles.scheduleLink)}>
          My schedule · availability and shifts
        </Link>
      </div>

      {isError ? (
        <Card padding={4}>
          <div {...stylex.props(styles.error)}>
            <Text size="sm" color="inherit">
              Couldn't load trips:{" "}
              {(error as Error)?.message ?? "unknown error"}
            </Text>
          </div>
        </Card>
      ) : trips.length === 0 ? (
        <div {...stylex.props(styles.empty)}>
          <Inbox size={32} />
          <Text size="sm" color="secondary">
            No trips assigned yet.
          </Text>
        </div>
      ) : (
        <ul {...stylex.props(styles.list)}>
          {trips.map((trip) => {
            const name = formatTripLabel(trip);
            return (
              <li key={trip.id}>
                <ClickableCard
                  label={name}
                  padding={4}
                  href={`/trips/${trip.id}`}
                  onClick={(e) => {
                    // Anchor keeps ⌘/ctrl/middle-click "open in new tab";
                    // intercept plain left-clicks for SPA nav (as Link does).
                    if (
                      e.metaKey ||
                      e.ctrlKey ||
                      e.shiftKey ||
                      e.altKey ||
                      e.button !== 0
                    )
                      return;
                    e.preventDefault();
                    navigate(`/trips/${trip.id}`);
                  }}
                >
                  <div {...stylex.props(styles.row)}>
                    <VStack gap={1} align="start">
                      <Text weight="medium">{name}</Text>
                      <TripProgress trip={trip} />
                    </VStack>
                    <span {...stylex.props(styles.right)}>
                      <Badge
                        variant={STATUS_VARIANT[trip.status] ?? "neutral"}
                        label={trip.status}
                      />
                      <span {...stylex.props(styles.muted)}>
                        <ChevronRight size={16} />
                      </span>
                    </span>
                  </div>
                </ClickableCard>
              </li>
            );
          })}
        </ul>
      )}
    </AppShell>
  );
}
